/** @jsxImportSource @emotion/react */
import {useState} from 'react';
import {css} from '@emotion/react';
import {COLUMN_SPACING} from '../../forms/styles';
import LabeledData from '../../forms/elements/LabeledData';
import LabelGroup from '../../forms/elements/LabelGroup';
import {MAX_COMMENT_CHARS, MAX_SUBJECT_CHARS, MIN_COMMENT_CHARS} from './constants';
import {Column, Row} from '../../layout/flexbox';
import {SmallText} from '../../styles/styles';
import CharacterCount from '../../forms/elements/CharacterCount';
import ErrorView from '../../forms/ErrorView';
import * as Buttons from '../../forms/elements/buttons';
import {Loader} from '../../forms/elements/loader/Loader';
import * as React from 'react';
import { getResource } from '../../language/resources/getResource';
import { IAppState } from '../../core/redux/IAppState';
import { useSelector } from 'react-redux';

const inputStyle = css`
  width: 100%;
  height: 35px;
  ${SmallText};
  border: solid black 1px;
  border-radius: 6px;
  padding-left: 10px;
  padding-right: 10px;
  box-sizing: border-box;
`;

const addCommentTextArea = css`
  min-height: 250px;
  padding: 10px;
  ${SmallText};
  border-radius: 6px;
  resize: none;
`;

const addCommentLoader = css`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-50%);
`

type Props = {
  guestName: string;
  isLoading: boolean;
  error: string | undefined;
  onSubmit?: (subject: string, comments: string) => void;
}

export const AddCommentForm = (props: Props) => {
  const [subject, setSubject] = useState('');
  const [comments, setComments] = useState('');
  const resources = useSelector((state: IAppState) => state.locale);

  const onChangeSubject = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event?.target?.value || '';
    setSubject(newValue);
  }

  const onChangeComments = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event?.target?.value || '';
    setComments(newValue);
  }

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault(); // Stop browser from adding query params to URL >.>
    props.onSubmit!(subject.trim(), comments.trim());
  }

  return (
    <form css={css`
            display: flex;
            flex-direction: column;
            ${COLUMN_SPACING};
          `}>

      {/* Guest Name */}
      <LabeledData label={getResource(resources, 'ADD_COMMENT_GUEST_LABEL')} data={props.guestName}/>

      {/* Subject */}
      <LabelGroup label={getResource(resources, 'ADD_COMMENT_SUBJECT_LABEL')}>
        <input autoFocus={true}
               data-testid='comments-add-subject-input'
               disabled={props.isLoading}
               maxLength={MAX_SUBJECT_CHARS}
               value={subject}
               onChange={onChangeSubject}
               css={inputStyle}/>
      </LabelGroup>

      {/* Comments */}
      <LabelGroup label={getResource(resources, 'ADD_COMMENT_COMMENTS_LABEL')} subLabel={getResource(resources, 'ADD_COMMENT_CHARACTER_INFO', MIN_COMMENT_CHARS, MAX_COMMENT_CHARS)}>
        <Column flexGrow={1}>
            <textarea data-testid='comments-add-body-input'
                      disabled={props.isLoading}
                      value={comments || ''}
                      onChange={onChangeComments}
                      css={addCommentTextArea}/>
          <CharacterCount resources={resources}
                          valueToCount={comments}
                          minRequired={MIN_COMMENT_CHARS}
                          maxAllowed={MAX_COMMENT_CHARS}
                          rootCss={css`margin-top: 5px;`}/>
        </Column>
      </LabelGroup>

      {/* Validation Message */}
      <ErrorView error={props.error}/>

      {/* Submit */}
      <Row alignItems='baseline' justifyContent='flex-end'>
        <Buttons.BigNeutral type="submit"
                            disabled={props.isLoading}
                            onClick={onSubmit}
                            title={getResource(resources, 'ADD_COMMENT_BUTTON_TITLE', props.guestName)}>{getResource(resources, 'ADD_COMMENT_ADD_COMMENT')}</Buttons.BigNeutral>
      </Row>
      {
        props.isLoading && <Loader rootCss={addCommentLoader}/>
      }
    </form>
  );

}